import { __decorate } from "tslib";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Row, Col, TimePicker, Checkbox, Table, Button, Icon } from "ant-design-vue";
import SumRadioGroup from "@/components/sum-radio-group/sum-radio-group.vue";
import DurationSelectRange from "@/components/content-exposure/duration-select-range/duration-select-range";
import MethodologyCheckGroup from "@/components/content-exposure/methodology-check-group/methodology-check-group.vue";
import ContentExposureObjective from "@/components/content-exposure/content-exposure-objective/content-exposure-objective.vue";
import ContentExposureActivity from "@/components/content-exposure/content-exposure-activity/content-exposure-activity.vue";
import ContentExposureRelatedItem from "@/components/content-exposure/content-exposure-relatedItem/content-exposure-relatedItem.vue";
import RichTextPreviewer from "@/components/rich-text-previewer/rich-text-previewer";
import LessonPlanEditorAction from "./components/lesson-plan-editor-action/lesson-plan-editor-action.vue";
import LessonPlanEditorVPCNeeded from "./components/lesson-plan-editor-vpcneeded/lesson-plan-editor-vpcneeded.vue";
import Spinning from "@/components/spinning/spinning.vue";
import { fmtMsg } from "commonui";
import { ContentExposureLocale, LessonPlanLocale } from "@/locales/localeid";
import { getModule } from "vuex-module-decorators";
import MasterPlanState from "@/modules/master-plan";
import { LessonPlanItemType4DB } from "@/models/master-plan";
import { ContentExposureRelatedItemType, ContentGroupEnum, LessonPlanVersionStatus, MediaSlot, MethodologyTypeEnum, SpecialDesignation } from "@/util/enum";
import { PdfIcon } from "@/style";
import { disabledDueToAccessLevel, getRouteAccessLevel, getContentConversationTitle, toTimeString } from "@/util/func";
import { ConversationTitle } from "@/util/constants";
import ManageOrderDrawer from "@/components/content-exposure/manage-order-drawer/manage-order-drawer.vue";
import MatIcon from "@/commonui/components/mat-icon/mat-icon";
const CheckboxGroup = Checkbox.Group;
function getOptionalItems({ type }) {
    return type === LessonPlanItemType4DB.OptionalContent;
}
function getOtherItems({ type }) {
    return type !== LessonPlanItemType4DB.OptionalContent;
}
let LessonPlanEditor = class LessonPlanEditor extends Vue {
    constructor() {
        super(...arguments);
        this.masterPlanModule = getModule(MasterPlanState);
        this.manageOrderDrawerVisible = false;
        this.currentSelectedExposureId = null;
        this.parentContentId = "";
        this.columns = [
            {
                title: fmtMsg(LessonPlanLocale.LessonPlanMasterPlanLessonPlanEditorColumnComponent),
                dataIndex: "duration",
                key: "component",
                width: 200,
                customRender: (text, record, index) => this.customComponentRender(text, record, index)
            },
            {
                title: fmtMsg(ContentExposureLocale.ContentExposureRelatedItems),
                dataIndex: "relatedItems",
                key: "relatedItems",
                customCell: (record) => record.type === LessonPlanItemType4DB.Activity
                    ? { style: { display: "none" } }
                    : {},
                scopedSlots: { customRender: "relatedItems" }
            },
            {
                title: fmtMsg(ContentExposureLocale.ContentExposureTeachingActivity),
                dataIndex: "teachingActivities",
                key: "teachingActivities",
                customCell: (record) => record.type === LessonPlanItemType4DB.Activity
                    ? { style: { display: "none" } }
                    : {},
                scopedSlots: { customRender: "teachingActivities" }
            },
            {
                title: fmtMsg(ContentExposureLocale.ContentExposureObjective),
                dataIndex: "objectives",
                key: "objectives",
                customCell: (record) => record.type === LessonPlanItemType4DB.Activity
                    ? { style: { display: "none" } }
                    : {},
                scopedSlots: { customRender: "objectives" }
            },
            {
                title: "",
                dataIndex: "action",
                key: "action",
                width: 80,
                align: "center",
                customCell: () => ({
                    class: "lesson-plan-editor-action"
                }),
                scopedSlots: { customRender: "action" }
            }
        ];
        this.loading = false;
    }
    customComponentRender(text, record, index) {
        return {
            children: (<div>
					{this.getComponentTitle(record)}
					{!this.hideContentType(record) && this.isContentTypeLabelVisible(record) && this.getComponentDetails(record)}
					<DurationSelectRange isEdit={record.isEdit && !this.isREPMethodology} isRepExposureDuration={this.masterPlanModule.getCurrentMethodology === MethodologyTypeEnum.REP} start={(record.duration || { start: "0:0" }).start || "0:0"} end={(record.duration || { end: "0:0" }).end || "0:0"} ref={`duration${record.id}`} onChange={(e) => this.masterPlanModule.setDuration({ record, values: e })} methodology=""/>
				</div>),
            attrs: record.type === LessonPlanItemType4DB.Activity
                ? {
                    colSpan: 4
                }
                : {}
        };
    }
    get contentExposureLocale() {
        return ContentExposureLocale;
    }
    get lessonPlanItems() {
        return this.otherItems.concat(this.optionalItems);
    }
    get isREPMethodology() {
        return this.masterPlanModule.currentMethodology === MethodologyTypeEnum.REP;
    }
    get otherItems() {
        return this.lessonPlanEditItems.filter(getOtherItems);
    }
    get optionalItems() {
        return this.lessonPlanEditItems.filter(getOptionalItems);
    }
    get enabled() {
        return this.masterPlanModule.currentLessonPlanVersion.status === LessonPlanVersionStatus.Draft;
    }
    get isEditing() {
        return this.lessonPlanItems.some((d) => d.isEdit);
    }
    get vpcNeeded() {
        return this.lessonPlanItems
            .filter((d) => d.type === LessonPlanItemType4DB.ExposureContent)
            .reduce((vpcs, item) => {
            const vpcitems = item.relatedItems
                .filter((d) => d.type === ContentExposureRelatedItemType.Component && d.contentType && d.contentType.specialDesignationId === SpecialDesignation.VPC)
                .map((d) => ({ title: d.title, sequence: d.content.sequence, typeSequence: d.typeSequence }));
            return vpcs.concat(vpcitems);
        }, []);
    }
    get currentRouteAccessLevel() {
        return getRouteAccessLevel(this.$route);
    }
    get disabledDueToAccessLevel() {
        return disabledDueToAccessLevel(this.currentRouteAccessLevel);
    }
    exposureHasErrors() {
        return Object.keys(this.$refs).some((key) => this.$refs[key]?.showError);
    }
    async onSaveLessonPlanItem(record) {
        if (this.exposureHasErrors()) {
            return;
        }
        this.loading = true;
        await this.masterPlanModule.updateLessonPlanItem(record).catch((_) => (this.loading = false));
        this.loading = false;
    }
    async onEditLessonPlanItem(record, isEdit) {
        if (isEdit) {
            record.isEdit = isEdit;
            this.masterPlanModule.editingLessonPlanItem(record);
        }
        else {
            this.loading = true;
            await this.masterPlanModule.fetchLessonPlanItems(record).catch((err) => {
                this.loading = false;
            });
            this.loading = false;
        }
    }
    async onMoveUpLessonPlanItem(record) {
        var preLessonPlanItemId = this.lessonPlanItems[this.lessonPlanItems.findIndex((d) => d.id === record.id) - 1]
            .id;
        await this.masterPlanModule.swapLessonPlanItems({
            lessonPlanId: record.lessonPlanId,
            preLessonPlanItemId,
            nextLessonPlanItemId: record.id
        });
    }
    async onMoveDownLessonPlanItem(record) {
        var nextLessonPlanItemId = this.lessonPlanItems[this.lessonPlanItems.findIndex((d) => d.id === record.id) + 1]
            .id;
        await this.masterPlanModule.swapLessonPlanItems({
            lessonPlanId: record.lessonPlanId,
            preLessonPlanItemId: record.id,
            nextLessonPlanItemId
        });
    }
    cannotMoveSingleLessonPlanItem(record) {
        return !((record.type === LessonPlanItemType4DB.OptionalContent ||
            record.type === LessonPlanItemType4DB.Activity) &&
            !this.showMoveUp(record) &&
            !this.showMoveDown(record));
    }
    hasAction(record) {
        return this.enabled && this.cannotMoveSingleLessonPlanItem(record);
    }
    showEdit(record) {
        return record.type === LessonPlanItemType4DB.ExposureContent;
    }
    showMoveUp(record) {
        return this.getItemsByType(record.type).findIndex((d) => d.id === record.id) != 0;
    }
    showMoveDown(record) {
        const items = this.getItemsByType(record.type);
        return items.findIndex((d) => d.id === record.id) != items.length - 1;
    }
    getItemsByType(type) {
        return type === LessonPlanItemType4DB.OptionalContent ? this.optionalItems : this.otherItems;
    }
    getComponentId(record) {
        switch (record.activityId) {
            case 2: return record.transition.id;
            case 3: return "";
            case 4: return "";
            default: return record.componentContent.id;
        }
    }
    getComponent(record) {
        return record.componentContent;
    }
    getComponentTitle(record) {
        if (record.activityId === null) {
            return (<div class="lesson-plan-editor-component-title">{record.componentContent.title}</div>);
        }
        switch (record.activityId) {
            case 2: return <RichTextPreviewer content={`${record.transition.text} (${record.transition.duration})`}></RichTextPreviewer>;
            case 3: return <b>{fmtMsg(LessonPlanLocale.LessonPlanPlayListBuilderCellQuestionTitle)}</b>;
            case 4: return <b>
				{record.contentConversation
                ? getContentConversationTitle(record.contentConversation.conversationName, record.contentConversation.contentName)
                : ConversationTitle}
			</b>;
            default: return <div class="lesson-plan-editor-component-title">{`${record.activityName} (${toTimeString(record.activityMaxDuration - record.activityMinDuration)})`}</div>;
        }
    }
    getComponentDetails(record) {
        return (<div class="lesson-plan-editor-component-details">
				<span>{`(${record.componentContent.contentType.name})`}</span>
				{this.isREPMethodology && (<span>{this.getComponentGroupIcon(record.mediaSlotId, record.componentContent.contentType.contentGroupId)}</span>)}
			</div>);
    }
    getComponentGroupIcon(mediaSlotId, contentGroupId) {
        if (mediaSlotId ? mediaSlotId === MediaSlot.ExposureAudio : contentGroupId === ContentGroupEnum.Song) {
            return <Icon type="play-circle" class="content-group-icon"/>;
        }
        else if (mediaSlotId ? mediaSlotId === MediaSlot.ExposureVideo : contentGroupId === ContentGroupEnum.Movie) {
            return <Icon type="video-camera" class="content-group-icon"/>;
        }
        else if (contentGroupId === ContentGroupEnum.VPC) {
            return <MatIcon class="content-group-icon" type="font_download"></MatIcon>;
        }
        else if (mediaSlotId === MediaSlot.ViewingContent) {
            return <img v-else class="content-group-icon" src={PdfIcon}/>;
        }
        else if (contentGroupId === ContentGroupEnum.Story) {
            return <Icon type="book" class="content-group-icon"/>;
        }
        else {
            return <img v-else class="content-group-icon" src={PdfIcon}/>;
        }
    }
    hideContentType(record) {
        return (record.type === LessonPlanItemType4DB.Activity ||
            record.componentContent.title.toLowerCase().startsWith("phonogram"));
    }
    isContentTypeLabelVisible(record) {
        const contentInfo = record.type === LessonPlanItemType4DB.OptionalContent ? record.optionalContent : (record.contentExposure ? record.contentExposure.content : null);
        return contentInfo && contentInfo.contentType
            && typeof contentInfo.contentType.showLabelInLessonPlan == "boolean" &&
            contentInfo.contentType.showLabelInLessonPlan;
    }
    onExpSubmit(record, values) {
        const contentExposureTeachingActivity = record.contentExposure.contentExposureTeachingActivity.map((activity) => {
            const teachingActivity = values.find((item) => item.id === activity.teachingActivityId);
            if (teachingActivity) {
                const { pageId, imageName, metaData, objectiveId } = teachingActivity;
                return { ...activity, pageId, imageName, metaData, objectiveId };
            }
            else {
                return { ...activity };
            }
        });
        this.masterPlanModule.setTeachingActivity({ record, values });
        this.masterPlanModule.setExposureTeachingActivity({ record, values: contentExposureTeachingActivity });
    }
    formatMessage(id, params) {
        return fmtMsg(id, params);
    }
    onSaveAssociatedTAObjectives(record, values) {
        const { selectedObjectiveIds } = values;
        let { teachingActivities } = values;
        // remove association of objectives not present in list selectedObjectiveIds from Teaching Activities
        const contentExposureTeachingActivity = record.contentExposure.contentExposureTeachingActivity.map((activity) => {
            const teachingActivity = teachingActivities.find((item) => item.id === activity.teachingActivityId);
            if (teachingActivity) {
                const { pageId, imageName, metaData } = teachingActivity;
                let { objectiveId } = teachingActivity;
                if (objectiveId && selectedObjectiveIds.indexOf(objectiveId) < 0) {
                    // update in teachingActivities
                    teachingActivities = teachingActivities.map((ta) => {
                        if (ta.objectiveId === objectiveId) {
                            ta.objectiveId = null;
                        }
                        return ta;
                    });
                    objectiveId = null;
                }
                return { ...activity, pageId, imageName, metaData, objectiveId };
            }
            else {
                return { ...activity };
            }
        });
        this.masterPlanModule.setTeachingActivity({ record, values: teachingActivities });
        this.masterPlanModule.setExposureTeachingActivity({ record, values: contentExposureTeachingActivity });
    }
    onManageOrder(record) {
        this.manageOrderDrawerVisible = true;
        this.currentSelectedExposureId = record.contentExposure.id;
        this.parentContentId = record.componentId ? record.componentId : null;
    }
    onManageDrawerClose() {
        this.manageOrderDrawerVisible = false;
        this.currentSelectedExposureId = null;
    }
};
__decorate([
    Prop({ default: () => [] })
], LessonPlanEditor.prototype, "lessonPlanEditItems", void 0);
LessonPlanEditor = __decorate([
    Component({
        components: {
            Row,
            Col,
            TimePicker,
            CheckboxGroup,
            Checkbox,
            SumRadioGroup,
            DurationSelectRange,
            MethodologyCheckGroup,
            ContentExposureObjective,
            ContentExposureActivity,
            ContentExposureRelatedItem,
            LessonPlanEditorAction,
            Spinning,
            Table,
            Button,
            Icon,
            LessonPlanEditorVPCNeeded,
            ManageOrderDrawer,
            MatIcon,
            RichTextPreviewer
        }
    })
], LessonPlanEditor);
export default LessonPlanEditor;
